import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Activity, DollarSign, Users, Utensils,
  AlertTriangle, CheckCircle2, Sparkles, FileWarning,
  LogOut, Sliders, Download, ArrowRight, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/authContext";
import { useAppContext } from "@/lib/onboardingContext";
import { useLocation } from "@/lib/locationContext";
import { supabase } from "@/integrations/supabase/client";
import LocationSwitcher from "@/components/LocationSwitcher";
import SurvivalScoreCard from "@/components/SurvivalScoreCard";
import { getCurrencySymbol, convertFromUSD } from "@/lib/currency";
import { useBenchmarks } from "@/lib/useBenchmarks";
import BenchmarkSettingsDialog from "@/components/BenchmarkSettingsDialog";
import DailyRevenueLog from "@/components/operations/DailyRevenueLog";
import PreShiftBriefing from "@/components/operations/PreShiftBriefing";
import MenuEngineering from "@/components/operations/MenuEngineering";
import ReviewScoreTracker from "@/components/operations/ReviewScoreTracker";
import PnLStatement from "@/components/operations/PnLStatement";
import { exportOperationsHealthPdf } from "@/lib/operationsHealthPdf";
import { toast } from "sonner";

// Brand colors per spec.
const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

type MonthlyCheckin = {
  month_label: string;
  revenue: number;
  labor_cost: number;
  food_cost: number;
  rent: number;
  created_at: string;
};

type PermitRenewal = {
  id: string;
  permit_name: string;
  expiration_date: string;
};

function pct(part: number, whole: number): number {
  if (!whole) return 0;
  return Math.round((part / whole) * 1000) / 10;
}

/** Operations Health Score 0–100. Penalises labor/food % above benchmarks
 *  and unresolved compliance issues. Mirrors Survival Score scale so it
 *  reads consistently across the app. */
function operationsHealth(opts: {
  revenue: number;
  laborCost: number;
  foodCost: number;
  expiringPermits: number;
  laborTarget: number;
  foodTarget: number;
}): number {
  const { revenue, laborCost, foodCost, expiringPermits, laborTarget, foodTarget } = opts;
  if (revenue <= 0) return 0;
  const lp = (laborCost / revenue) * 100;
  const fp = (foodCost / revenue) * 100;
  // 50 pts labor, 35 pts food, 15 pts compliance.
  // Each metric is fully scored when at-or-under target, dropping to 0 once
  // the user is 30 percentage points past their target.
  const ls = Math.max(0, Math.min(50, 50 * (1 - Math.max(0, lp - laborTarget) / 30)));
  const fs = Math.max(0, Math.min(35, 35 * (1 - Math.max(0, fp - foodTarget) / 30)));
  const cs = Math.max(0, 15 - expiringPermits * 5);
  return Math.round(ls + fs + cs);
}

function healthTone(score: number) {
  if (score >= 75) return { label: "Healthy", color: BRAND_GREEN };
  if (score >= 50) return { label: "At Risk", color: BRAND_ORANGE };
  return { label: "Critical", color: "#c0392b" };
}

function MetricCard({
  icon: Icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: any;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground font-medium">
          {label}
        </span>
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent ?? BRAND_GREEN}1a` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent ?? BRAND_GREEN }} />
        </div>
      </div>
      <div
        className="text-2xl sm:text-3xl font-medium tabular-nums"
        style={{ color: BRAND_DARK }}
      >
        {value}
      </div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function OperationsDashboard() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { onboarding } = useAppContext();
  const { activeLocation } = useLocation();
  const { benchmarks } = useBenchmarks();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [latest, setLatest] = useState<MonthlyCheckin | null>(null);
  const [permits, setPermits] = useState<PermitRenewal[]>([]);
  const [loading, setLoading] = useState(true);

  // Welcome banner — shown once per user, until they enter their first
  // Survival Score data or explicitly dismiss it.
  const welcomeKey = user ? `oplif_ops_welcome_dismissed_${user.id}` : "";
  const [welcomeDismissed, setWelcomeDismissed] = useState<boolean>(() => {
    try {
      return welcomeKey ? localStorage.getItem(welcomeKey) === "true" : false;
    } catch {
      return false;
    }
  });
  const dismissWelcome = () => {
    setWelcomeDismissed(true);
    try {
      if (welcomeKey) localStorage.setItem(welcomeKey, "true");
    } catch {}
  };
  const showWelcome = !loading && !latest && !welcomeDismissed;

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Latest monthly checkin (optionally scoped to active location).
      let q = supabase
        .from("monthly_checkins")
        .select("month_label, revenue, labor_cost, food_cost, rent, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (activeLocation?.id) {
        q = q.eq("location_id", activeLocation.id);
      }
      const { data: checkins } = await q;

      const { data: renewals } = await supabase
        .from("permit_renewals")
        .select("id, permit_name, expiration_date")
        .eq("user_id", user.id)
        .order("expiration_date", { ascending: true });

      if (cancelled) return;
      setLatest((checkins?.[0] as MonthlyCheckin) ?? null);
      setPermits((renewals as PermitRenewal[]) ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, activeLocation?.id]);

  const cs = getCurrencySymbol(
    onboarding.country || activeLocation?.city || onboarding.city || "",
  );
  const fmtMoney = (n: number) =>
    n >= 1000 ? `${cs}${(n / 1000).toFixed(1)}K` : `${cs}${n.toLocaleString()}`;

  // Fall back to onboarding revenue estimate when no real check-in exists yet.
  const fallbackRevenue = Number(onboarding.monthlyRevenueEstimate) || 0;
  const revenue = latest?.revenue ?? fallbackRevenue;
  const laborCost = latest?.labor_cost ?? 0;
  const foodCost = latest?.food_cost ?? 0;

  const laborPct = pct(laborCost, revenue);
  const foodPct = pct(foodCost, revenue);

  const expiringPermits = useMemo(() => {
    const now = Date.now();
    const sixtyDays = 1000 * 60 * 60 * 24 * 60;
    return permits.filter((p) => {
      const t = new Date(p.expiration_date).getTime();
      return !isNaN(t) && t - now < sixtyDays;
    });
  }, [permits]);

  const healthScore = operationsHealth({
    revenue,
    laborCost,
    foodCost,
    expiringPermits: expiringPermits.length,
    laborTarget: benchmarks.laborPct,
    foodTarget: benchmarks.foodPct,
  });
  const tone = healthTone(healthScore);

  const challengeCopy: Record<string, string> = {
    labor: "Labor costs",
    food: "Food costs",
    turnover: "Staff turnover",
    compliance: "Compliance",
    revenue: "Revenue growth",
  };

  // AI Recommendations — quick rule-based for now; survival-score edge fn
  // already produces deeper recs inside the SurvivalScoreCard component.
  const recs = useMemo(() => {
    const out: { title: string; detail: string; tone: "warn" | "ok" }[] = [];
    if (laborPct > benchmarks.laborPct) {
      out.push({
        tone: "warn",
        title: "Trim labor exposure",
        detail: `Labor is ${laborPct}% of revenue — your target is ${benchmarks.laborPct}%. Audit your weakest shifts and consider cross-training.`,
      });
    }
    if (foodPct > benchmarks.foodPct) {
      out.push({
        tone: "warn",
        title: "Tighten food cost",
        detail: `Food cost is ${foodPct}% — your target is ${benchmarks.foodPct}%. Try a waste log and renegotiate your two highest-spend SKUs.`,
      });
    }
    if (expiringPermits.length > 0) {
      out.push({
        tone: "warn",
        title: `${expiringPermits.length} permit${expiringPermits.length > 1 ? "s" : ""} expiring soon`,
        detail: "Renew before the deadline to avoid fines and forced closures.",
      });
    }
    if (out.length === 0 && revenue > 0) {
      out.push({
        tone: "ok",
        title: "Operations look healthy",
        detail: "Keep logging monthly to spot drift early. Consider setting a stretch revenue goal.",
      });
    }
    if (out.length === 0) {
      out.push({
        tone: "ok",
        title: "Log your first month to unlock recommendations",
        detail: "Enter revenue, labor, food, and rent in the Survival Score below for a tailored read.",
      });
    }
    return out;
  }, [laborPct, foodPct, expiringPermits.length, revenue, benchmarks.laborPct, benchmarks.foodPct]);

  const handleExportPdf = async () => {
    if (!user) return;
    try {
      // Pull the latest P&L for the current month (if any).
      const monthKey = new Date().toISOString().slice(0, 7); // YYYY-MM
      let pnlQuery = (supabase as any)
        .from("pnl_statements")
        .select("*")
        .eq("user_id", user.id)
        .eq("month_label", monthKey)
        .limit(1);
      if (activeLocation?.id) pnlQuery = pnlQuery.eq("location_id", activeLocation.id);
      else pnlQuery = pnlQuery.is("location_id", null);
      const { data: pnlRows } = await pnlQuery;
      const r = pnlRows?.[0];
      let pnlPayload: Parameters<typeof exportOperationsHealthPdf>[0]["pnl"] = null;
      if (r) {
        const foodRev = Number(r.food_revenue) || 0;
        const bevRev = Number(r.beverage_revenue) || 0;
        const foodC = Number(r.food_cost) || 0;
        const bevC = Number(r.beverage_cost) || 0;
        const foh = Number(r.foh_labor) || 0;
        const boh = Number(r.boh_labor) || 0;
        const mgmt = Number(r.management_cost) || 0;
        const rent = Number(r.rent) || 0;
        const utils = Number(r.utilities) || 0;
        const mkt = Number(r.marketing) || 0;
        const rep = Number(r.repairs_maintenance) || 0;
        const ins = Number(r.insurance) || 0;
        const oth = Number(r.other_expenses) || 0;
        const totalRevenue = foodRev + bevRev;
        const totalCogs = foodC + bevC;
        const totalLabor = foh + boh + mgmt;
        const totalOpex = rent + utils + mkt + rep + ins + oth;
        const grossProfit = totalRevenue - totalCogs;
        const operatingProfit = grossProfit - totalLabor - totalOpex;
        const netMargin = totalRevenue > 0 ? (operatingProfit / totalRevenue) * 100 : 0;
        pnlPayload = {
          monthLabel: new Date(`${r.month_label}-01`).toLocaleDateString(undefined, {
            month: "long", year: "numeric",
          }),
          foodRevenue: foodRev, beverageRevenue: bevRev,
          foodCost: foodC, beverageCost: bevC,
          fohLabor: foh, bohLabor: boh, managementCost: mgmt,
          rent, utilities: utils, marketing: mkt, repairs: rep,
          insurance: ins, otherExpenses: oth,
          totalRevenue, totalCogs, totalLabor, totalOpex,
          grossProfit, operatingProfit, netMargin,
        };
      }

      // Latest survival score
      let scoreQuery = (supabase as any)
        .from("survival_scores")
        .select("score, month_label")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (activeLocation?.id) scoreQuery = scoreQuery.eq("location_id", activeLocation.id);
      const { data: scoreRows } = await scoreQuery;
      const survival = scoreRows?.[0] ?? null;

      const healthColor: "green" | "orange" | "red" =
        healthScore >= 75 ? "green" : healthScore >= 50 ? "orange" : "red";

      exportOperationsHealthPdf({
        businessName: activeLocation?.name || onboarding.businessName || "Your business",
        city: activeLocation?.city || onboarding.city || undefined,
        currencySymbol: cs,
        generatedAt: new Date(),
        healthScore,
        healthLabel: tone.label,
        healthColor,
        monthlyRevenue: revenue,
        laborPct,
        laborTarget: benchmarks.laborPct,
        foodCostPct: foodPct,
        foodTarget: benchmarks.foodPct,
        pnl: pnlPayload,
        survivalScore: survival?.score ?? null,
        survivalLabel: survival?.month_label ?? null,
        recommendations: recs,
        permits: permits.map((p) => ({
          name: p.permit_name,
          daysToExpiry: Math.ceil(
            (new Date(p.expiration_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
          ),
        })),
      });
    } catch (e) {
      console.error("PDF export failed", e);
      toast.error("Couldn't generate PDF. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-background pb-16" id="ops-dashboard-root">
      {/* Print-specific stylesheet — applied only when generating a PDF
          via window.print(). Hides chrome (header, switchers, action
          buttons), forces a clean white background, lets sections break
          naturally across pages, and keeps brand accents intact. */}
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }

          html, body {
            background: #ffffff !important;
            color: #1a1a1a !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Hide app chrome that doesn't belong in a PDF report */
          .print-hide,
          header,
          nav,
          [data-sonner-toaster],
          [role="tooltip"] {
            display: none !important;
          }

          /* Strip motion / shadows / scroll containers for a flat doc look */
          #ops-dashboard-root .shadow-sm,
          #ops-dashboard-root .shadow-md,
          #ops-dashboard-root .shadow-lg {
            box-shadow: none !important;
          }

          #ops-dashboard-root .container {
            max-width: none !important;
            padding: 0 !important;
          }

          /* Print header */
          #ops-print-header {
            display: block !important;
            margin-bottom: 16px;
            padding-bottom: 10px;
            border-bottom: 2px solid #2d8653;
          }
          #ops-print-header h1 {
            font-size: 20pt;
            font-weight: 700;
            color: #2d8653;
            margin: 0 0 4px 0;
          }
          #ops-print-header .meta {
            font-size: 10pt;
            color: #555;
          }

          /* Avoid awkward breaks inside cards & charts */
          #ops-dashboard-root .rounded-2xl,
          #ops-dashboard-root .rounded-xl,
          #ops-dashboard-root table,
          #ops-dashboard-root .recharts-wrapper {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* Tighten spacing & ensure borders show */
          #ops-dashboard-root .border,
          #ops-dashboard-root .border-border {
            border-color: #d4d4d4 !important;
          }

          /* Force colored swatches (health score, recs accents) to render */
          #ops-dashboard-root [style*="background"] {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Section spacing */
          #ops-dashboard-root .space-y-8 > * + * { margin-top: 18px !important; }
          #ops-dashboard-root .pt-6 { padding-top: 0 !important; }

          /* Prevent input fields from looking like editable widgets */
          #ops-dashboard-root input,
          #ops-dashboard-root select,
          #ops-dashboard-root textarea {
            border: none !important;
            background: transparent !important;
            padding: 0 !important;
            color: #1a1a1a !important;
          }
        }

        /* Hidden by default — only shown when printing */
        #ops-print-header { display: none; }
      `}</style>

      {/* Print-only header band */}
      <div id="ops-print-header">
        <h1>Operations Health Report</h1>
        <div className="meta">
          {(activeLocation?.name || onboarding.businessName || "Your business")}
          {" · "}
          {new Date().toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          })}
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-border bg-card print-hide">
        <div className="container py-3 sm:py-4 flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0 order-2 sm:order-none basis-full sm:basis-auto">
            <h1
              className="font-heading text-base sm:text-lg font-medium truncate"
              style={{ color: BRAND_DARK }}
            >
              {activeLocation?.name || onboarding.businessName || "Your business"}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">
              Operations dashboard
              {onboarding.biggestChallenge && (
                <> · Focus: {challengeCopy[onboarding.biggestChallenge] ?? onboarding.biggestChallenge}</>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 ml-auto shrink-0">
            <LocationSwitcher />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportPdf}
              className="gap-2 h-9 px-2 sm:px-3"
              title="Export PDF report"
              aria-label="Export PDF"
            >
              <Download className="w-4 h-4" />
              <span className="hidden md:inline">Export PDF</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSettingsOpen(true)}
              aria-label="Benchmark settings"
              title="Benchmark settings"
              className="h-9 w-9"
            >
              <Sliders className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={async () => { await signOut(); navigate("/"); }}
              aria-label="Sign out"
              className="h-9 w-9"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <BenchmarkSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <div className="container max-w-6xl pt-4 sm:pt-6 space-y-6 sm:space-y-8">
        {/* WELCOME BANNER — first-time visit, no survival data yet */}
        {showWelcome && (
          <div
            className="relative rounded-2xl p-4 sm:p-5 text-white shadow-md print-hide"
            style={{ backgroundColor: BRAND_GREEN }}
            role="region"
            aria-label="Welcome banner"
          >
            <button
              type="button"
              onClick={dismissWelcome}
              aria-label="Dismiss welcome banner"
              className="absolute top-3 right-3 inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <h2 className="font-heading text-base sm:text-lg font-medium pr-10">
              Welcome to your Operations Dashboard 🎯
            </h2>
            <p className="mt-1.5 text-sm text-white/90 max-w-2xl">
              Start by entering this month's numbers in your Survival Score
              below — it takes 2 minutes and unlocks your AI recommendations.
            </p>
          </div>
        )}

        {/* MODE INDICATOR */}
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 rounded-xl border border-border bg-card px-3 sm:px-4 py-2.5 sm:py-3 print-hide">
          <div className="flex items-center gap-3 min-w-0">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
              style={{
                backgroundColor: `${BRAND_GREEN}1a`,
                color: BRAND_GREEN,
              }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: BRAND_GREEN }}
              />
              Managing Existing Business
            </span>
            <span className="text-sm text-muted-foreground truncate">
              {activeLocation?.name || onboarding.businessName || "Your business"}
              {(activeLocation?.city || onboarding.city) && (
                <> · {activeLocation?.city || onboarding.city}</>
              )}
            </span>
          </div>
          <Link
            to="/dashboard?mode=opening"
            className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Switch to Opening Planner
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* TOP ROW METRICS */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          <div
            className="rounded-2xl p-4 sm:p-5 text-white shadow-md"
            style={{ backgroundColor: tone.color }}
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] sm:text-xs uppercase tracking-wider font-medium opacity-90">
                Operations Health
              </span>
              <Activity className="w-4 h-4 opacity-90" />
            </div>
            <div className="text-3xl sm:text-4xl font-medium tabular-nums">
              {loading ? "—" : healthScore}
              <span className="text-sm sm:text-base opacity-80 ml-1">/ 100</span>
            </div>
            <div className="mt-1 text-xs opacity-90">{tone.label}</div>
          </div>
          <MetricCard
            icon={DollarSign}
            label="Monthly Revenue"
            value={loading ? "—" : revenue > 0 ? fmtMoney(revenue) : "—"}
            sub={latest ? latest.month_label : "From onboarding estimate"}
            accent={BRAND_GREEN}
          />
          <MetricCard
            icon={Users}
            label="Labor % of revenue"
            value={loading ? "—" : revenue > 0 ? `${laborPct}%` : "—"}
            sub={`Your target: ≤ ${benchmarks.laborPct}%`}
            accent={laborPct > benchmarks.laborPct ? BRAND_ORANGE : BRAND_GREEN}
          />
          <MetricCard
            icon={Utensils}
            label="Food Cost % of revenue"
            value={loading ? "—" : revenue > 0 ? `${foodPct}%` : "—"}
            sub={`Your target: ≤ ${benchmarks.foodPct}%`}
            accent={foodPct > benchmarks.foodPct ? BRAND_ORANGE : BRAND_GREEN}
          />
        </motion.div>

        {/* SURVIVAL SCORE */}
        <SurvivalScoreCard />

        {/* FINANCIAL HEALTH — Full P&L */}
        <PnLStatement />

        {/* COMPLIANCE TRACKER */}
        <div className="grid grid-cols-1 gap-6">
          <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${BRAND_ORANGE}1a` }}
              >
                <FileWarning className="w-4 h-4" style={{ color: BRAND_ORANGE }} />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
                  Compliance Tracker
                </h2>
                <p className="text-xs text-muted-foreground">License & permit renewals</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/permits")}>
                Manage
              </Button>
            </div>
            {permits.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No permits tracked yet. Add your renewals in the Permits page to enable compliance alerts.
              </p>
            ) : (
              <ul className="space-y-2">
                {permits.slice(0, 5).map((p) => {
                  const days = Math.ceil(
                    (new Date(p.expiration_date).getTime() - Date.now()) /
                      (1000 * 60 * 60 * 24),
                  );
                  const expired = days < 0;
                  const soon = days >= 0 && days < 60;
                  return (
                    <li
                      key={p.id}
                      className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm"
                    >
                      <span className="font-medium" style={{ color: BRAND_DARK }}>
                        {p.permit_name}
                      </span>
                      <span
                        className="text-xs font-medium"
                        style={{
                          color: expired
                            ? "#c0392b"
                            : soon
                              ? BRAND_ORANGE
                              : BRAND_GREEN,
                        }}
                      >
                        {expired
                          ? `Expired ${Math.abs(days)}d ago`
                          : `Renew in ${days}d`}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* AI RECOMMENDATIONS */}
        <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${BRAND_GREEN}1a` }}
            >
              <Sparkles className="w-4 h-4" style={{ color: BRAND_GREEN }} />
            </div>
            <div>
              <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
                AI Recommendations
              </h2>
              <p className="text-xs text-muted-foreground">Tailored to your latest numbers</p>
            </div>
          </div>
          <ul className="space-y-3">
            {recs.map((rec, i) => {
              const Icon = rec.tone === "warn" ? AlertTriangle : CheckCircle2;
              const color = rec.tone === "warn" ? BRAND_ORANGE : BRAND_GREEN;
              return (
                <li
                  key={i}
                  className="flex items-start gap-3 rounded-xl border border-border bg-background p-4"
                >
                  <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
                  <div>
                    <p className="font-medium text-sm" style={{ color: BRAND_DARK }}>
                      {rec.title}
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {rec.detail}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* MODULE 1 — Daily Revenue Log */}
        <DailyRevenueLog />

        {/* MODULE 2 — Pre-shift Briefing Generator */}
        <PreShiftBriefing />

        {/* MODULE 3 — Menu Engineering */}
        <MenuEngineering />

        {/* MODULE 4 — Review Score Tracker */}
        <ReviewScoreTracker />
      </div>
    </div>
  );
}